import type { LatLon } from '../geo';

/**
 * Zugriff auf das vorgebackene Hoehen- und Tiefenraster.
 *
 * Die Daten stammen aus `tools/build_map.py` (ETOPO 2022) und liegen als
 * PNG vor, in dem die Hoehe ueber zwei Kanaele kodiert ist. Diese Klasse
 * kennt nur die dekodierten Werte und bleibt damit frei von Phaser und DOM —
 * sie laeuft unveraendert in den Tests.
 */

export interface MapBounds {
  readonly north: number;
  readonly south: number;
  readonly west: number;
  readonly east: number;
}

export interface MapMeta {
  readonly bounds: MapBounds;
  readonly grid: {
    readonly width: number;
    readonly height: number;
    readonly resolutionArcsec: number;
  };
  readonly elevation: {
    readonly encoding: string;
    readonly offset: number;
    readonly minMetres: number;
    readonly maxMetres: number;
  };
}

/** Hoehe des Meeresspiegels. Alles darueber ist Land. */
const SEA_LEVEL = 0;

export class Bathymetry {
  readonly meta: MapMeta;

  /**
   * Hoehen in Metern, zeilenweise von Nord nach Sued.
   * Int16 genuegt: der tiefste Punkt der Erde liegt bei -10.994 m, der
   * hoechste bei 8.848 m — beides weit innerhalb des Wertebereichs.
   */
  private readonly elevations: Int16Array;

  constructor(meta: MapMeta, elevations: Int16Array) {
    const expected = meta.grid.width * meta.grid.height;
    if (elevations.length !== expected) {
      throw new Error(
        `Bathymetry: ${elevations.length} Werte, erwartet ${expected} (${meta.grid.width}x${meta.grid.height})`,
      );
    }
    this.meta = meta;
    this.elevations = elevations;
  }

  /**
   * Dekodiert die Bilddaten aus `bathymetry.png`.
   * Erwartet RGBA, wie es `CanvasRenderingContext2D.getImageData()` liefert.
   */
  static fromEncodedRGBA(rgba: Uint8ClampedArray | Uint8Array, meta: MapMeta): Bathymetry {
    const count = meta.grid.width * meta.grid.height;
    if (rgba.length < count * 4) {
      throw new Error(`Bathymetry: Bilddaten zu kurz (${rgba.length} Bytes für ${count} Punkte)`);
    }

    const elevations = new Int16Array(count);
    for (let i = 0; i < count; i++) {
      const high = rgba[i * 4] as number;
      const low = rgba[i * 4 + 1] as number;
      elevations[i] = ((high << 8) | low) - meta.elevation.offset;
    }

    return new Bathymetry(meta, elevations);
  }

  get width(): number {
    return this.meta.grid.width;
  }

  get height(): number {
    return this.meta.grid.height;
  }

  /** Hoehe am Rasterpunkt, ohne Interpolation. Indizes werden auf den Rand geklemmt. */
  elevationAtIndex(column: number, row: number): number {
    const x = Math.max(0, Math.min(this.width - 1, Math.trunc(column)));
    const y = Math.max(0, Math.min(this.height - 1, Math.trunc(row)));
    return this.elevations[y * this.width + x] as number;
  }

  /** Gebrochene Rasterkoordinate einer geografischen Position. */
  toGrid(p: LatLon): { column: number; row: number } {
    const { north, south, west, east } = this.meta.bounds;
    return {
      column: ((p.lon - west) / (east - west)) * (this.width - 1),
      row: ((north - p.lat) / (north - south)) * (this.height - 1),
    };
  }

  /**
   * Hoehe in Metern, bilinear interpoliert. Negative Werte sind Wassertiefen.
   * Positionen ausserhalb der Karte liefern den Randwert.
   */
  elevationAt(p: LatLon): number {
    const { column, row } = this.toGrid(p);

    const x0 = Math.floor(column);
    const y0 = Math.floor(row);
    const fx = column - x0;
    const fy = row - y0;

    const topLeft = this.elevationAtIndex(x0, y0);
    const topRight = this.elevationAtIndex(x0 + 1, y0);
    const bottomLeft = this.elevationAtIndex(x0, y0 + 1);
    const bottomRight = this.elevationAtIndex(x0 + 1, y0 + 1);

    const top = topLeft + (topRight - topLeft) * fx;
    const bottom = bottomLeft + (bottomRight - bottomLeft) * fx;
    return top + (bottom - top) * fy;
  }

  /** Wassertiefe in Metern (positiv). An Land 0. */
  depthAt(p: LatLon): number {
    return Math.max(0, -this.elevationAt(p));
  }

  isLand(p: LatLon): boolean {
    return this.elevationAt(p) > SEA_LEVEL;
  }

  isNavigable(p: LatLon, minimumDepthMetres = 0): boolean {
    return this.depthAt(p) > minimumDepthMetres;
  }

  /**
   * Tiefenprofil entlang einer Strecke — Grundlage dafuer, ob ein U-Boot einen
   * Ort erreicht und ob ein Schiff dort ueberhaupt fahren kann.
   * Interpoliert linear in Rasterkoordinaten; ueber die kurzen Strecken im
   * Spiel ist der Unterschied zum Grosskreis vernachlaessigbar.
   */
  profileAlong(from: LatLon, to: LatLon, samples: number): number[] {
    const count = Math.max(2, Math.trunc(samples));
    const profile: number[] = new Array(count);

    for (let i = 0; i < count; i++) {
      const t = i / (count - 1);
      profile[i] = this.elevationAt({
        lat: from.lat + (to.lat - from.lat) * t,
        lon: from.lon + (to.lon - from.lon) * t,
      });
    }

    return profile;
  }

  /** Groesste Wassertiefe entlang einer Strecke. */
  maxDepthAlong(from: LatLon, to: LatLon, samples = 64): number {
    let deepest = 0;
    for (const elevation of this.profileAlong(from, to, samples)) {
      const depth = Math.max(0, -elevation);
      if (depth > deepest) deepest = depth;
    }
    return deepest;
  }

  /** Kreuzt die Strecke Land? Verhindert Kurse quer durch eine Insel. */
  crossesLand(from: LatLon, to: LatLon, samples = 64): boolean {
    return this.profileAlong(from, to, samples).some((elevation) => elevation > SEA_LEVEL);
  }

  /**
   * Flachste Stelle entlang einer Strecke, als Wassertiefe in Metern.
   * An Land negativ — so laesst sich beides in einem Wert ausdruecken.
   *
   * Wichtig fuer die Routenbewertung: Die direkte Verbindung zwischen zwei
   * Punkten im Dreieck fuehrt haeufig quer ueber die Bahama-Bank, wo stellenweise
   * nur wenige Meter Wasser stehen.
   */
  shallowestAlong(from: LatLon, to: LatLon, samples = 96): number {
    let shallowest = Number.POSITIVE_INFINITY;
    for (const elevation of this.profileAlong(from, to, samples)) {
      const depth = -elevation;
      if (depth < shallowest) shallowest = depth;
    }
    return shallowest;
  }

  /** Ist die direkte Strecke fuer ein Fahrzeug mit diesem Tiefgang befahrbar? */
  routeIsNavigable(from: LatLon, to: LatLon, draughtMetres: number, samples = 96): boolean {
    return this.shallowestAlong(from, to, samples) > draughtMetres;
  }
}
